import { keepPreviousData, useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { api } from 'shared/api/client';
import ServerErrorHandler from 'shared/feedback/ErrorHandler';
import { styleKeys } from 'entities/style';
import { documentKeys } from './keys';
import { requireValue } from 'shared/lib/assert';
import { isRevisionMismatch } from 'shared/api/revision-mismatch';
import { invalidateOnBatchCreate } from 'entities/tag';

export const useGetSectionPages = (documentRevisionId: string | undefined, sectionRevisionIds: unknown) => {
  return useQuery({
    queryKey: documentKeys.sectionPages(documentRevisionId, sectionRevisionIds),

    queryFn: async () => {
      return api
        .POST('/documents/{superId}/pages', {
          params: { path: { superId: requireValue(documentRevisionId, 'section pages queried without a document revision') } },
          body: { sectionIds: sectionRevisionIds as string[] },
        })
        .then((res) => res.data);
    },

    enabled: !!documentRevisionId,
    placeholderData: keepPreviousData,
  });
};

export const useMovePageToSection = ({ onPageRemoved }: { onPageRemoved?: (page: unknown, document: unknown) => void }) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      documentId,
      fromPage,
      targetPage,
      copyPage,
    }: {
      documentId: number | string;
      fromPage: unknown;
      targetPage: unknown;
      copyPage?: boolean;
    }) =>
      await api.POST('/documents/{superId}/actions', {
        params: { path: { superId: Number(documentId) } },
        body: { action: 'set_page_order', data: { fromPage, targetPage, copyPage } },
      }),

    onSuccess: (res, ctx) => {
      queryClient.invalidateQueries({
        queryKey: [...documentKeys.pagesAll],
      });
      if (!ctx.copyPage && typeof onPageRemoved === 'function') {
        onPageRemoved(ctx.fromPage, res.data);
      }
    },

    onError: (e) => {
      if (!isRevisionMismatch(e)) ServerErrorHandler(e);
    },
  });
};

export type ClonePageResult = Record<string, unknown> & { clonedPage?: unknown };

export const useClonePage = ({ documentSuperId }: { documentSuperId: number | string }) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      pageId,
      fromPage,
      targetPage,
      idMappings,
      sourcePage,
      clonedPage,
    }: {
      pageId: string;
      fromPage?: unknown;
      targetPage?: unknown;
      idMappings?: unknown;
      sourcePage?: unknown;
      clonedPage?: unknown;
    }) => {
      const action = {
        action: 'clone_page',
        data: {
          documentSuperId,
          pageId,
          fromPage,
          targetPage,
          idMappings,
          sourcePage,
          clonedPage,
        },
      };
      const res = await api.POST('/documents/{superId}/actions', {
        params: { path: { superId: Number(documentSuperId) } },
        body: action,
      });
      return res?.data as ClonePageResult | undefined;
    },

    onError: ServerErrorHandler,

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...styleKeys.cssAll],
      });
      queryClient.invalidateQueries({
        queryKey: [...styleKeys.styleAll],
      });
      invalidateOnBatchCreate(queryClient, Number(documentSuperId));
    },
  });
};

export const useDeletePage = ({ documentSuperId }: { documentSuperId: number | string }) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ pageId, sectionSuperId, idMappings }: { pageId: string; sectionSuperId?: string; idMappings?: unknown }) => {
      const action = {
        action: 'delete_page',
        data: {
          documentSuperId,
          pageId,
          sectionSuperId,
          idMappings,
        },
      };
      return await api.POST('/documents/{superId}/actions', {
        params: { path: { superId: Number(documentSuperId) } },
        body: action,
      });
    },

    onError: ServerErrorHandler,

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...documentKeys.pagesAll],
      });
      queryClient.invalidateQueries({
        queryKey: [...styleKeys.cssAll],
      });
      queryClient.invalidateQueries({
        queryKey: [...styleKeys.styleAll],
      });
    },
  });
};
